import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { ImpactIcon, ImpactIcons } from "../ImpactIcon";
import {
  Section,
  SectionHeadingProps,
  BackgroundVariantProps,
} from "../Section";
import { Button } from "../Button";
import { Typography } from "../Typography";
import { isImpactShop } from "../utils/is-impact-shop";
import { isZeroPage } from "../utils/is-zero";
import PlayVideo from "@ecologi/assets/icons/play_video.svg";
import { isZeroRoadmap } from "../utils/is-zero-roadmap";

const MW_365 = `only screen and (max-width: 365px)`;

const Grid = styled.div<{
  isCompact?: boolean;
  isImpactPage?: boolean;
  isLarge?: boolean;
}>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(367px, 1fr));
  justify-content: center;
  width: 100%;
  margin-bottom: ${(props) => (props?.isImpactPage ? "30px" : "0px")};

  @media ${(props) => props.theme.bp.desktopUp} {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }

  @media ${(props) => props.theme.bp.tabletUp} {
    ${(props) =>
      props.isCompact &&
      css`
        max-width: 945px;
        margin: auto;
      `}
    ${(props) =>
      props?.isLarge &&
      css`
        max-width: 1250px;
        gap: 40px;
      `}
  }
`;

const dividerColorMapper = {
  MUSHROOM_DARK: "mushroomDark",
  MIST_DARK: "mistDark",
  WHITE: "snowdrop",
};

const Column = styled.div<{ dividerColor?: string; isLarge?: boolean }>`
  align-items: center;
  border-bottom: 3px solid
    ${(props) =>
      props.theme.colors[dividerColorMapper[props.dividerColor as string]]};
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  margin: auto;
  max-width: ${(props) => (props?.isLarge ? 370 : 320)}px;
  padding: 30px 15px 40px 15px;

  &:last-child {
    border-bottom: none;
  }

  @media ${MW_365} {
    ${(props) =>
      props?.isLarge &&
      css`
        max-width: 320px;
      `};
  }

  @media ${(props) => props.theme.bp.tabletUp} {
    border-bottom: none;
    ${(props) =>
      props?.isLarge &&
      css`
        max-width: 370px;
      `};
  }

  @media ${(props) => props.theme.bp.desktopUp} {
    border-bottom: none;
    border-right: 2px solid
      ${(props) =>
        props.theme.colors[dividerColorMapper[props.dividerColor as string]]};

    padding: 0px 15px 0px 15px;

    &:last-child {
      border-right: none;
    }
  }
`;

const zeroVariants = {
  zeroTitle: css`
    min-height: 35px;
  `,
  zeroDescription: css`
    margin-bottom: 30px;
  `,
};

const Title = styled.h3<{ isImpactPage?: boolean; slug: string }>`
  font-size: 22px;
  font-weight: bold;
  line-height: 1.28;
  margin: 0 0 16px;
  min-height: ${(props) => (props.isImpactPage ? 0 : 57)}px;
  text-align: center;
  ${(props) => zeroVariants[`${props.slug}Title`]};
`;

const Description = styled(Typography)<{ slug: string }>`
  text-align: center;
  margin-bottom: 20px;
  ${(props) => zeroVariants[`${props.slug}Description`]};
`;

const newIcons = [
  ImpactIcons.MEASURE_CIRCLE,
  ImpactIcons.MARKETPLACE_CIRCLE,
  ImpactIcons.INTEGRATE_CIRCLE,
  ImpactIcons.WORKFORCE_CIRCLE,
  ImpactIcons.EARTH,
  ImpactIcons.COMPAS,
  ImpactIcons.MAP,
  ImpactIcons.PENCIL,
  ImpactIcons.CLAP,
  ImpactIcons.WORLD,
  ImpactIcons.INTEGRATION_COG,
  ImpactIcons.ZERO_CHART,
];

const Icon = styled(ImpactIcon)`
  margin-bottom: 30px;

  ${(props) =>
    newIcons.includes(props.icon) &&
    css`
      height: 150px;
      width: 150px;
    `}
`;

const ButtonWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: flex-end;
`;

export type BusinessImpactOptionsProps = {
  columns: ColumnProps[];
  slug?: string | string[];
  divider_color?: string;
  isLarge?: boolean;
} & SectionHeadingProps &
  BackgroundVariantProps;

type ColumnProps = {
  icon: ImpactIcons;
  title: string;
  description: string;
  link: string;
  linkText: string;
  divider_color?: string;
};

export const BusinessImpactOptions = ({
  columns,
  backgroundVariant,
  slug,
  isLarge,
  ...props
}: BusinessImpactOptionsProps) => {
  return (
    <Section isNewExtraLarge slug={slug} backgroundVariant={backgroundVariant}>
      {props.title && (
        <Section.Heading {...props} marginBottomOverride="50px" slug={slug} />
      )}
      <Grid
        isCompact={columns.length < 4}
        isImpactPage={isImpactShop(slug)}
        isLarge={isLarge}
      >
        {columns.map((column: ColumnProps, i) => (
          <Column
            key={`column-${i}`}
            dividerColor={column?.divider_color}
            isLarge={isLarge}
          >
            <Icon aria-labelledby={`column-title-${i}`} icon={column.icon} />
            <Title
              id={`column-title-${i}`}
              dangerouslySetInnerHTML={{ __html: column.title }}
              isImpactPage={isImpactShop(slug)}
              slug={slug as string}
            />
            <Description use="body1" as="p" slug={slug as string}>
              {column.description}
            </Description>
            <ButtonWrapper>
              <Button
                href={column.link}
                text={column.linkText}
                green2
                darkBlue={isZeroPage(slug) || isZeroRoadmap(slug)}
                icon={column.linkText === "Watch video" ? PlayVideo : null}
              />
            </ButtonWrapper>
          </Column>
        ))}
      </Grid>
    </Section>
  );
};
